import React, { useEffect, useState, useRef } from "react";
import {
  PatternlibOverlayContainer,
  PatternlibButton,
  PatternlibIcon,
  PatternlibTextinput,
  PatternlibTextarea,
  PatternlibSpinner,
  PatternlibTextlink,
} from "@liebherr/patternlib-react";

import { useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useLocalStorage } from "../../helpers/helpers";
import PropTypes from "prop-types";
import { v4 as uuidv4 } from "uuid";
import { getMidentData, getImage } from "../../helpers/helpers";
import { apiURL, apiKey, catalog } from "../../config/api";
import styles from "./AddComparisonButton.module.css";
import { pushToDataLayer } from "../../helpers/helpers";

const AddComparisonButton = ({ part, path, data, onSave }) => {
  const { t, i18n } = useTranslation();
  const [items, setItems] = useLocalStorage("saved", []);
  const [config, setConfig] = useState({});
  const [customName, setCustomName] = useState("");
  const [isDisabled, setIsDisabled] = useState(false);
  const [show, setShow] = useState(false);
  const overlayRef = useRef();
  const location = useLocation();

  // the useRef Hook allows to persist data between renders
  const partRef = useRef();
  const pathRef = useRef();
  const dataRef = useRef();
  const customNameRef = useRef();
  const customNotesRef = useRef();
  const imageRef = useRef();

  const configSaveGA = (productKey, configName) => {
    const eventName = "configuration_save";
    const pagePath = location.pathname;
    const pageType = "configurator";
    const language =
      i18n.language === "de-DE" || i18n.language === "de" ? "de-DE" : "en-EN";
    const product_key = productKey;
    const configuration_name = configName;
    let props = {
      pagePath,
      pageType,
      language,
      product_key,
      configuration_name,
    };

    pushToDataLayer(eventName, props);
  };

  const configCompareGA = (configName) => {
    const eventName = "configuration_compare";
    const pagePath = location.pathname;
    const pageType = "my_configurations";
    const language =
      i18n.language === "de-DE" || i18n.language === "de" ? "de-DE" : "en-EN";
    const configuration_name = configName;
    let props = {
      pagePath,
      pageType,
      language,
      configuration_name,
    };

    pushToDataLayer(eventName, props);
  };

  const generateCustomName = (part) => {
    const key = part ? part : "";
    return key;
  };

  const getMident = (part) => {
    setIsDisabled(true);
    getMidentData(apiURL, part, apiKey, catalog).then((data) => {
      const mident = data["mident"] ? data["mident"] : null;
      const format = "JPEG";
      if (mident !== null) {
        getImage(apiURL, format, apiKey, mident).then((image) => {
          imageRef.current = image;
          setIsDisabled(false);
        });
      }
    });
  };

  const handleChangeCustomName = (event) => {
    let name = event.detail.value;
    if (name === "" || name === null) {
      name = generateCustomName();
    }
    customNameRef.current = name;
  };
  const handleChangeCustomNotes = (event) => {
    customNotesRef.current = event.detail.value;
  };

  const onButtonClick = () => {
    configSaveGA(partRef.current, customNameRef.current);
    setShow(true);
    getMident(part);
  };

  const onConfirm = () => {
    if (!isDisabled) {
      const id = uuidv4();
      const item = {
        part: partRef.current,
        path: pathRef.current,
        data: dataRef.current,
        type: partRef.current.substring(0, 3),
        customName: customNameRef.current,
        customNotes: customNotesRef.current,
        id: id,
        image: imageRef.current,
      };
      configCompareGA(customNameRef.current);
      setConfig(item);
      setShow(false);
    }
  };

  const onClose = () => {
    setShow(false);
  };

  useEffect(() => {
    if (part !== undefined) {
      const name = generateCustomName(part);
      partRef.current = part;

      customNameRef.current = name;
      setCustomName(name);
    }
  }, [part]);

  useEffect(() => {
    if (path !== undefined) {
      pathRef.current = path;
    }
  }, [path]);

  useEffect(() => {
    if (data !== undefined) {
      dataRef.current = data;
    }
  }, [data]);

  useEffect(() => {
    if (Object.keys(config).length >= 1) {
      const arr = items;
      const newArr = [...arr];
      newArr.push(config);
      setItems(newArr);

      onSave("Confirm");
    }
  }, [config]);

  useEffect(() => {
    if (show) {
      const ref = overlayRef.current;
      const element = ref.shadowRoot.querySelector(".backdrop");
      const content = ref.shadowRoot.querySelector(".content");
      content.style.zIndex = "9999";

      if (element !== null) {
        element.addEventListener("click", onClose, false);

        return () => {
          element.removeEventListener("click", onClose, true);
        };
      }
    }
  }, [show]);

  return (
    <>
      <PatternlibOverlayContainer
        target="center"
        show={show}
        disable-click-handlers
        ref={overlayRef}
      >
        <PatternlibButton
          onClick={(e) => onButtonClick()}
          icon-position="left"
          label={t("AddComparisonButton.saveConfig")}
          type="secondary"
          size="small"
          slot="element"
          display="block"
          width="100%"
        >
          <PatternlibIcon icon-name="star-plus" size="24px" slot="icon" />
        </PatternlibButton>
        <div slot="content" className={styles.modalContent}>
          <div className={styles.modalHeader}>
            <h2>{t("AddComparisonButton.addToComparison")}</h2>
            <div className={styles.closeIcon}>
              <PatternlibIcon icon-name="x" onClick={onClose}></PatternlibIcon>
            </div>
          </div>
          <div className={styles.modalContainer}>
            <div className={styles.modalWrapper}>
              <p>{t("AddComparisonButton.comparisonInfo")}</p>
              <PatternlibTextinput
                label={t("AddComparisonButton.customNameLabel")}
                placeholder={t("AddComparisonButton.customNamePlaceholder")}
                required
                name="customName"
                required-error-text={t(
                  "AddComparisonButton.customNameRequired"
                )}
                value={customName}
                type="text"
                maxlength="255"
                show-remove-icon="false"
                onLhChange={(e) => handleChangeCustomName(e)}
              ></PatternlibTextinput>
              <PatternlibTextarea
                label={t("AddComparisonButton.customNotesLabel")}
                readonly="false"
                required="false"
                width="100%"
                name="customNotes"
                height="undefined"
                additional-info=""
                onLhChange={(e) => handleChangeCustomNotes(e)}
              ></PatternlibTextarea>
              <div>
                <div className={styles.gridContainer}>
                  {isDisabled && (
                    <div className={styles.item1 && styles.gridItem}>
                      <PatternlibSpinner spin></PatternlibSpinner>
                    </div>
                  )}
                  <div className={styles.item2 && styles.gridItem}>
                    {isDisabled ? t("Spinner.loadPreview") : ""}
                  </div>
                </div>
                <img
                  src={imageRef.current}
                  style={{ display: isDisabled ? "none" : "block" }}
                  alt="img"
                ></img>
              </div>
            </div>
          </div>
          <div className={styles.modalFooter}>
            <PatternlibTextlink
              label={t("AddComparisonButton.cancelButton")}
              size="small"
              onClick={onClose}
            ></PatternlibTextlink>
            <PatternlibButton
              label-text-align="left"
              icon-position="right"
              label={t("AddComparisonButton.saveConfiguration")}
              disabled={isDisabled}
              size="small"
              onClick={onConfirm}
            >
              <PatternlibIcon
                icon-name="font-arrow"
                slot="icon"
              ></PatternlibIcon>
            </PatternlibButton>
          </div>
        </div>
      </PatternlibOverlayContainer>
    </>
  );
};

AddComparisonButton.propTypes = {
  part: PropTypes.string,
  path: PropTypes.string,
  data: PropTypes.any,
  onSave: PropTypes.func.isRequired,
};

AddComparisonButton.defaultProps = {
  part: "",
  path: "",
  data: null,
};

export default AddComparisonButton;
